import { Button, Input, Drawer, Dropdown, Skeleton, Spin, Tooltip } from "antd";
import {
  AppstoreOutlined,
  CloseOutlined,
  DownOutlined,
  ExportOutlined,
  FileOutlined,
  FilterOutlined,
  MenuOutlined,
  RightOutlined,
  SearchOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  lazy,
  Suspense,
  useState,
  useMemo,
  useCallback,
  memo,
  useId,
  useRef,
  useEffect,
} from "react";
import "./GlobalSearchBar.css";
import useGlobalSearchBar from "./GlobalSearchBarFunctions";
import LazyObligationDetails from "components/universal/ObligationDetails/LazyObligationDetails";
import FrameworkCircles from "components/universal/FrameworkCircles/FrameworkCircles";
import DynamicTooltip from "components/universal/DynamicTooltip/DynamicTooltip";
import { useTranslation } from "react-i18next";
import { renderFileIcon } from "utils/utils";
import {
  getObligationDrawerTarget,
  GLOBAL_SEARCH_TYPES,
  isFilesSearchType,
} from "./globalSearchUtils";
import { getModuleIcon } from "modules/appModuleIcons";
import useScrollArrows from "hooks/useScrollArrows";

const ObligationsTypeIcon = getModuleIcon("HiOutlineClipboardDocumentCheck");
const FilesTypeIcon = getModuleIcon("HiOutlineFolder");

const SEARCH_PANEL_ID = "global-search-panel";

const GlobalSearchEvidencePreviewContent = lazy(() =>
  import("./GlobalSearchEvidencePreviewContent")
);

const PREVIEW_DRAWER_BODY_STYLES = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
  height: "calc(100dvh - 55px)",
  padding: 16,
};

const PreviewDrawerSkeleton = ({ label }) => (
  <div className="gsb2-preview-chunk-loading" role="status" aria-live="polite">
    <Spin />
    <span>{label}</span>
  </div>
);

const FRAMEWORK_CIRCLE_STYLE = {
  width: 16,
  height: 16,
  fontSize: 7,
  fontWeight: 700,
  borderRadius: "50%",
  flexShrink: 0,
};

const STATUS_BADGE_CONFIG = {
  completed: {
    bg: "#BFD9C0",
    text: "#297A2B",
    border: "#BFD9C0",
  },
  completedLate: {
    bg: "#F1D9BF",
    text: "#C7842C",
    border: "#F1D9BF",
  },
  overdue: {
    bg: "#E1C4C6",
    text: "#852A2E",
    border: "#E1C4C6",
  },
  inprogress: {
    bg: "#BCCEE5",
    text: "#093E7D",
    border: "#BCCEE5",
  },
  scheduled: {
    bg: "#E6E6E6",
    text: "#999999",
    border: "#E6E6E6",
  },
};

const STATUS_BADGE_ORDER = [
  { type: "completed", key: "completedCount", fallbackKey: "completed" },
  { type: "completedLate", key: "completedLateCount", fallbackKey: "completed_late" },
  { type: "overdue", key: "lateCount", fallbackKey: "late" },
  { type: "inprogress", key: "workingOnCount", fallbackKey: "working_on" },
  { type: "scheduled", key: "scheduledCount", fallbackKey: "scheduled" },
];

const RESULT_SECTION_TITLE_KEYS = {
  [GLOBAL_SEARCH_TYPES.ALL]: "global_search.labels.section_results_all",
  [GLOBAL_SEARCH_TYPES.OBLIGATIONS]: "global_search.labels.section_results",
  [GLOBAL_SEARCH_TYPES.FILES]: "global_search.labels.section_results_files",
};

const getStatusBadgeStyle = (type) => {
  const t = String(type || "");
  if (STATUS_BADGE_CONFIG[t]) return STATUS_BADGE_CONFIG[t];
  return { bg: "#f5f5f5", text: "#374151", border: "#d9d9d9" };
};

const buildStatusBadges = (groupStatus = {}) =>
  STATUS_BADGE_ORDER.map(({ type, key, fallbackKey }) => {
    const raw = groupStatus[key] ?? groupStatus[fallbackKey] ?? 0;
    const count = Number(raw) || 0;
    return { type, count, display: count > 0 ? String(count) : "-" };
  });

const getReadableTextColor = (hex, fallback = "#ffffff") => {
  if (!hex) return fallback;
  const h = String(hex).replace("#", "").trim();
  const raw = h.length === 8 ? h.slice(0, 6) : h;
  if (raw.length !== 6) return fallback;

  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return fallback;

  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.62 ? "#111827" : "#ffffff";
};

function ResultsSkeleton({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="gsb2-row gsb2-row--skeleton" aria-hidden="true">
          <div className="gsb2-row-left">
            <Skeleton
              active
              title={{ width: `${52 + (i % 4) * 12}%`, style: { margin: 0 } }}
              paragraph={false}
            />
            <div className="gsb2-row-bottom">
              <div className="gsb2-row-meta gsb2-row-meta--skeleton">
                <Skeleton.Button active size="small" style={{ width: 44, height: 14 }} />
                <Skeleton.Avatar active size={14} shape="circle" />
                <Skeleton.Button active size="small" style={{ width: 72, height: 14 }} />
              </div>
              <div className="gsb2-row-status" aria-hidden="true">
                {STATUS_BADGE_ORDER.map(({ type }) => (
                  <span
                    key={type}
                    className="gsb2-status gsb2-status--skeleton"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

const FrameworkBadge = memo(function FrameworkBadge({ item }) {
  const frameworkName = (item.framework_name || "").trim();
  const backgroundColor = item.framework_bg || "#959595ff";
  const fontColor =
    item.framework_font || getReadableTextColor(backgroundColor, "#ffffff");

  const frameworks = useMemo(
    () => ({
      id: frameworkName || "FW",
      name: frameworkName || "FW",
      background_color: backgroundColor,
      font_color: fontColor,
    }),
    [frameworkName, backgroundColor, fontColor]
  );

  const tooltipTitle = frameworkName || "FW";

  return (
    <Tooltip title={tooltipTitle} getPopupContainer={() => document.body}>
      <span className="gsb2-ellipsis gsb2-ellipsis--fw">
        <FrameworkCircles
          frameworks={frameworks}
          styleOverrides={FRAMEWORK_CIRCLE_STYLE}
          gap={0}
          showTooltips={false}
        />
      </span>
    </Tooltip>
  );
});

const ObligationResultRow = memo(function ObligationResultRow({
  item,
  groupStatus,
  onOpen,
  t,
}) {
  const statusLoaded = groupStatus != null;
  const statusBadges = statusLoaded ? buildStatusBadges(groupStatus) : null;

  return (
    <button type="button" className="gsb2-row" onClick={() => onOpen(item)}>
      <div className="gsb2-row-left">
        <DynamicTooltip title={item.title} className="gsb2-ellipsis">
          <div className="gsb2-row-title">{item.title}</div>
        </DynamicTooltip>

        <div className="gsb2-row-bottom">
          <div className="gsb2-row-meta">
            {!!item.obl_tag && (
              <DynamicTooltip title={item.obl_tag} className="gsb2-ellipsis gsb2-ellipsis--tag">
                <span className="gsb2-tag gsb2-tag--blue">{item.obl_tag}</span>
              </DynamicTooltip>
            )}

            <FrameworkBadge item={item} />

            {!!item.due_date && (
              <DynamicTooltip title={item.due_date} className="gsb2-date">
                {item.due_date}
              </DynamicTooltip>
            )}

            {!!item.responsible_name && (
              <DynamicTooltip
                title={item.responsible_name}
                className="gsb2-ellipsis gsb2-ellipsis--tag"
              >
                <span className="gsb2-tag gsb2-tag--green">
                  {item.responsible_name}
                </span>
              </DynamicTooltip>
            )}
          </div>

          <div
            className="gsb2-row-status"
            aria-label={t("global_search.labels.obligation_status_counts")}
            aria-busy={!statusLoaded}
          >
            {statusLoaded
              ? statusBadges.map((status) => {
                  const style = getStatusBadgeStyle(status.type);
                  return (
                    <span
                      key={status.type}
                      className="gsb2-status"
                      style={{
                        background: style.bg,
                        color: style.text,
                        borderColor: style.border,
                      }}
                    >
                      {status.display}
                    </span>
                  );
                })
              : STATUS_BADGE_ORDER.map(({ type }) => (
                  <span
                    key={type}
                    className="gsb2-status gsb2-status--skeleton"
                    aria-hidden="true"
                  />
                ))}
          </div>
        </div>
      </div>
    </button>
  );
});

const FileResultRow = memo(function FileResultRow({ item, onOpen, onPreview, t }) {
  const fileName = item.file_name || item.title;
  const fileIcon = renderFileIcon(fileName, 14) || (
    <FileOutlined aria-hidden="true" />
  );
  const criterionLabel = item.criterion_tag
    ? t("global_search.labels.criterion_tag_prefix", { tag: item.criterion_tag })
    : "";
  const criterionTooltip = item.criterion_tag
    ? item.criterion_name
      ? t("global_search.labels.criterion_tooltip_with_name", {
          tag: item.criterion_tag,
          name: item.criterion_name,
        })
      : t("global_search.labels.criterion_tooltip", { tag: item.criterion_tag })
    : "";

  return (
    <article
      className="gsb2-row gsb2-evidence-row"
      role="button"
      tabIndex={0}
      onClick={() => onPreview(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPreview(item);
        }
      }}
    >
      <div className="gsb2-row-left">
        <div className="gsb2-evidence-title-row">
          <span className="gsb2-evidence-file-icon" aria-hidden="true">
            {fileIcon}
          </span>
          <DynamicTooltip title={fileName} className="gsb2-ellipsis gsb2-ellipsis--title">
            <div className="gsb2-row-title">{fileName}</div>
          </DynamicTooltip>
        </div>

        <div className="gsb2-row-bottom gsb2-evidence-bottom">
          <div className="gsb2-row-meta">
            {!!item.obl_tag && (
              <DynamicTooltip title={item.obl_tag} className="gsb2-ellipsis gsb2-ellipsis--tag">
                <span className="gsb2-tag gsb2-tag--blue">{item.obl_tag}</span>
              </DynamicTooltip>
            )}
            {!!item.criterion_tag && (
              <DynamicTooltip
                title={criterionTooltip}
                className="gsb2-ellipsis gsb2-ellipsis--tag"
              >
                <span className="gsb2-tag gsb2-tag--criterion">{criterionLabel}</span>
              </DynamicTooltip>
            )}
            <FrameworkBadge item={item} />
            {!!item.criterion_name && (
              <DynamicTooltip
                title={item.criterion_name}
                className="gsb2-ellipsis gsb2-ellipsis--criterion-name"
              >
                <span className="gsb2-evidence-criterion">{item.criterion_name}</span>
              </DynamicTooltip>
            )}
            {!!item.uploaded_at && (
              <DynamicTooltip title={item.uploaded_at} className="gsb2-date">
                {item.uploaded_at}
              </DynamicTooltip>
            )}
          </div>

          <div className="gsb2-evidence-actions">
            <button
              type="button"
              className="gsb2-row-action"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(item);
              }}
            >
              <ExportOutlined aria-hidden="true" />
              {t("global_search.actions.open_in_obligation")}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
});

const SearchResultRow = memo(function SearchResultRow({
  item,
  groupStatus,
  onOpen,
  onPreview,
  t,
}) {
  if (isFilesSearchType(item.type)) {
    return (
      <FileResultRow
        item={item}
        onOpen={onOpen}
        onPreview={onPreview}
        t={t}
      />
    );
  }

  return (
    <ObligationResultRow
      item={item}
      groupStatus={groupStatus}
      onOpen={onOpen}
      t={t}
    />
  );
});

const FilterSection = memo(function FilterSection({
  title,
  items,
  selectedKeys,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onClear,
  searchPlaceholder,
  renderLeading,
  collapsed,
  onToggleCollapsed,
  expandLabel,
  collapseLabel,
  emptySearchLabel,
  clearLabel,
  t,
}) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const list = items || [];
  const hasQuery = query.trim().length > 0;
  const isSingleton = list.length === 1;

  const filteredItems = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) =>
      String(item?.name || "")
        .toLowerCase()
        .includes(term)
    );
  }, [list, query]);

  const visibleKeys = useMemo(
    () => filteredItems.map((item) => item.id || item.name).filter(Boolean),
    [filteredItems]
  );

  const selectedSet = useMemo(() => new Set(selectedKeys || []), [selectedKeys]);
  const selectedVisibleCount = useMemo(
    () => visibleKeys.filter((key) => selectedSet.has(key)).length,
    [visibleKeys, selectedSet]
  );
  const selectedSectionCount = (selectedKeys || []).length;
  const totalVisible = visibleKeys.length;
  const allVisibleSelected =
    totalVisible > 0 && selectedVisibleCount === totalVisible;
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < totalVisible;
  const keepKey =
    (selectedKeys || []).find((key) => key != null) ??
    list[0]?.id ??
    list[0]?.name;

  const masterLabel = useMemo(() => {
    if (totalVisible === 0) return "";
    if (hasQuery) {
      if (allVisibleSelected || selectedVisibleCount === 0) {
        return t("global_search.filters.master.select_all_visible", {
          count: totalVisible,
        });
      }
      return t("global_search.filters.master.some_visible", {
        selected: selectedVisibleCount,
        total: totalVisible,
      });
    }
    if (allVisibleSelected) {
      return t("global_search.filters.master.all_selected", {
        count: totalVisible,
      });
    }
    if (someVisibleSelected) {
      return t("global_search.filters.master.some_selected", {
        selected: selectedVisibleCount,
        total: totalVisible,
      });
    }
    return t("global_search.filters.master.select_all");
  }, [
    allVisibleSelected,
    hasQuery,
    selectedVisibleCount,
    someVisibleSelected,
    t,
    totalVisible,
  ]);

  const handleMasterToggle = useCallback(() => {
    if (isSingleton || totalVisible === 0) return;
    if (allVisibleSelected) {
      // Leave at least one checked (same rule as Clear).
      if (hasQuery) {
        const keepVisible =
          visibleKeys.find((key) => selectedSet.has(key)) ?? visibleKeys[0];
        onDeselectAll?.(visibleKeys.filter((key) => key !== keepVisible));
      } else {
        onClear?.(keepKey);
      }
    } else {
      onSelectAll?.(visibleKeys);
    }
  }, [
    allVisibleSelected,
    hasQuery,
    isSingleton,
    keepKey,
    onClear,
    onDeselectAll,
    onSelectAll,
    selectedSet,
    totalVisible,
    visibleKeys,
  ]);

  const handleClear = useCallback(() => {
    if (selectedSectionCount <= 1) return;
    onClear?.(keepKey);
  }, [keepKey, onClear, selectedSectionCount]);

  const handleRowToggle = useCallback(
    (key) => {
      if (isSingleton) return;
      onToggle?.(key);
    },
    [isSingleton, onToggle]
  );

  return (
    <div className="gsb2-filter-section">
      <button
        type="button"
        className="gsb2-sectionHeader gsb2-sectionHeader--toggle"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        aria-controls={listId}
        aria-label={collapsed ? expandLabel : collapseLabel}
      >
        <span className="gsb2-sectionTitle">{title}</span>
        <span className="gsb2-sectionChevron" aria-hidden="true">
          {collapsed ? <RightOutlined /> : <DownOutlined />}
        </span>
      </button>

      {!collapsed && (
        <div id={listId} className="gsb2-filterList" role="group">
          <div className="gsb2-filter-toolbar">
            <Input
              size="small"
              allowClear
              className="gsb2-filter-search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              prefix={<SearchOutlined className="gsb2-filter-search-icon" />}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            {totalVisible > 0 && (
              <div className="gsb2-filter-master">
                <label
                  className={`gsb2-filter-master-label${isSingleton ? " gsb2-filter-master-label--disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="gsb2-filterCheckbox"
                    checked={allVisibleSelected}
                    disabled={isSingleton}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected;
                    }}
                    onChange={handleMasterToggle}
                  />
                  <DynamicTooltip
                    title={masterLabel}
                    className="gsb2-ellipsis gsb2-filter-master-text"
                    getPopupContainer={() => document.body}
                  >
                    <span className="gsb2-filter-master-text-inner">{masterLabel}</span>
                  </DynamicTooltip>
                </label>
                {selectedSectionCount > 1 && (
                  <button
                    type="button"
                    className="gsb2-filter-clear"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    {clearLabel}
                  </button>
                )}
              </div>
            )}
          </div>

          {filteredItems.length === 0 ? (
            <div className="gsb2-filter-empty">{emptySearchLabel}</div>
          ) : (
            filteredItems.map((item) => {
              const key = item.id || item.name;
              const isLastSelected =
                !isSingleton &&
                selectedKeys.includes(key) &&
                selectedSectionCount <= 1;
              const rowDisabled = isSingleton || isLastSelected;
              return (
                <label
                  key={key}
                  className={`gsb2-filterRow${rowDisabled ? " gsb2-filterRow--disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    className="gsb2-filterCheckbox"
                    checked={selectedKeys.includes(key)}
                    disabled={rowDisabled}
                    onChange={() => handleRowToggle(key)}
                  />
                  {typeof renderLeading === "function" ? renderLeading(item) : null}
                  <DynamicTooltip
                    title={item.name}
                    className="gsb2-ellipsis gsb2-filterLabel"
                    getPopupContainer={() => document.body}
                  >
                    <span className="gsb2-filterLabel-inner">{item.name}</span>
                  </DynamicTooltip>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
});

const FrameworkFilterLeading = memo(function FrameworkFilterLeading({ fw }) {
  const frameworks = useMemo(
    () => ({
      id: fw.id || fw.name,
      name: fw.name,
      background_color: fw.background_color || "#959595ff",
      font_color: fw.font_color || "#ffffff",
    }),
    [fw.id, fw.name, fw.background_color, fw.font_color]
  );

  return (
    <span
      className="gsb2-filter-fw-wrap"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <FrameworkCircles
        frameworks={frameworks}
        containerClassName="gsb2-filter-fw"
        gap={0}
        showTooltips={false}
      />
    </span>
  );
});

const FILTER_SECTION_KEYS = {
  frameworks: "frameworks",
  entities: "entities",
  responsibles: "responsibles",
};

const SearchFilterSidebar = memo(function SearchFilterSidebar({
  accessScopeFrameworks,
  accessScopeEntities,
  accessScopeUsers,
  selectedFrameworkKeys,
  selectedEntityKeys,
  selectedResponsibleKeys,
  onToggleFramework,
  onToggleEntity,
  onToggleResponsible,
  onSelectAllFrameworks,
  onSelectAllEntities,
  onSelectAllResponsibles,
  onDeselectFrameworks,
  onDeselectEntities,
  onDeselectResponsibles,
  onClearFrameworks,
  onClearEntities,
  onClearResponsibles,
  t,
}) {
  const [collapsedSections, setCollapsedSections] = useState({
    [FILTER_SECTION_KEYS.frameworks]: false,
    [FILTER_SECTION_KEYS.entities]: false,
    [FILTER_SECTION_KEYS.responsibles]: false,
  });
  const autoCollapsedRef = useRef({
    [FILTER_SECTION_KEYS.frameworks]: false,
    [FILTER_SECTION_KEYS.entities]: false,
    [FILTER_SECTION_KEYS.responsibles]: false,
  });

  // Collapse singleton sections once when they first have exactly 1 option.
  useEffect(() => {
    const lengths = {
      [FILTER_SECTION_KEYS.frameworks]: accessScopeFrameworks?.length ?? 0,
      [FILTER_SECTION_KEYS.entities]: accessScopeEntities?.length ?? 0,
      [FILTER_SECTION_KEYS.responsibles]: accessScopeUsers?.length ?? 0,
    };

    setCollapsedSections((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(lengths).forEach(([sectionKey, length]) => {
        if (length === 1 && !autoCollapsedRef.current[sectionKey]) {
          autoCollapsedRef.current[sectionKey] = true;
          next[sectionKey] = true;
          changed = true;
        } else if (length !== 1) {
          autoCollapsedRef.current[sectionKey] = false;
        }
      });
      return changed ? next : prev;
    });
  }, [
    accessScopeFrameworks?.length,
    accessScopeEntities?.length,
    accessScopeUsers?.length,
  ]);

  const {
    scrollViewportRef,
    canGoUp,
    canGoDown,
    getScrollHandlers,
  } = useScrollArrows([
    accessScopeFrameworks?.length,
    accessScopeEntities?.length,
    accessScopeUsers?.length,
    collapsedSections,
  ]);

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const renderFrameworkLeading = useCallback(
    (fw) => <FrameworkFilterLeading fw={fw} />,
    []
  );

  const expandLabel = t("global_search.actions.expand_section");
  const collapseLabel = t("global_search.actions.collapse_section");
  const clearLabel = t("global_search.actions.clear");
  const emptySearchLabel = t("global_search.messages.no_filter_matches");
  const scrollMoreLabel = t("global_search.actions.scroll_for_more_filters");
  const scrollTopLabel = t("global_search.actions.scroll_to_top_filters");
  const showUpArrow = canGoUp && !canGoDown;

  return (
    <div className="gsb2-filters">
      <div className="gsb2-rightScroll gsb2-scroll" ref={scrollViewportRef}>
        <FilterSection
          title={t("global_search.labels.filter_by_framework")}
          items={accessScopeFrameworks}
          selectedKeys={selectedFrameworkKeys}
          onToggle={onToggleFramework}
          onSelectAll={onSelectAllFrameworks}
          onDeselectAll={onDeselectFrameworks}
          onClear={onClearFrameworks}
          searchPlaceholder={t("global_search.placeholders.filter_frameworks")}
          renderLeading={renderFrameworkLeading}
          collapsed={collapsedSections[FILTER_SECTION_KEYS.frameworks]}
          onToggleCollapsed={() =>
            toggleSection(FILTER_SECTION_KEYS.frameworks)
          }
          expandLabel={expandLabel}
          collapseLabel={collapseLabel}
          emptySearchLabel={emptySearchLabel}
          clearLabel={clearLabel}
          t={t}
        />

        <div className="gsb2-rightDivider" />

        <FilterSection
          title={t("global_search.labels.filter_by_entity")}
          items={accessScopeEntities}
          selectedKeys={selectedEntityKeys}
          onToggle={onToggleEntity}
          onSelectAll={onSelectAllEntities}
          onDeselectAll={onDeselectEntities}
          onClear={onClearEntities}
          searchPlaceholder={t("global_search.placeholders.filter_entities")}
          collapsed={collapsedSections[FILTER_SECTION_KEYS.entities]}
          onToggleCollapsed={() => toggleSection(FILTER_SECTION_KEYS.entities)}
          expandLabel={expandLabel}
          collapseLabel={collapseLabel}
          emptySearchLabel={emptySearchLabel}
          clearLabel={clearLabel}
          t={t}
        />

        <div className="gsb2-rightDivider" />

        <FilterSection
          title={t("global_search.labels.filter_by_responsible")}
          items={accessScopeUsers}
          selectedKeys={selectedResponsibleKeys}
          onToggle={onToggleResponsible}
          onSelectAll={onSelectAllResponsibles}
          onDeselectAll={onDeselectResponsibles}
          onClear={onClearResponsibles}
          searchPlaceholder={t("global_search.placeholders.filter_responsibles")}
          collapsed={collapsedSections[FILTER_SECTION_KEYS.responsibles]}
          onToggleCollapsed={() =>
            toggleSection(FILTER_SECTION_KEYS.responsibles)
          }
          expandLabel={expandLabel}
          collapseLabel={collapseLabel}
          emptySearchLabel={emptySearchLabel}
          clearLabel={clearLabel}
          t={t}
        />
      </div>

      {canGoDown && (
        <Tooltip title={scrollMoreLabel}>
          <Button
            className="gsb2-filters-scroll-arrow gsb2-filters-scroll-arrow--down"
            type="text"
            icon={<DownOutlined style={{ fontSize: 10 }} />}
            aria-label={scrollMoreLabel}
            {...getScrollHandlers({ y: 1 })}
          />
        </Tooltip>
      )}

      {showUpArrow && (
        <Tooltip title={scrollTopLabel}>
          <Button
            className="gsb2-filters-scroll-arrow gsb2-filters-scroll-arrow--up"
            type="text"
            icon={<UpOutlined style={{ fontSize: 10 }} />}
            aria-label={scrollTopLabel}
            {...getScrollHandlers({ y: -1 })}
          />
        </Tooltip>
      )}
    </div>
  );
});

export default function GlobalSearchBarDesign() {
  const {
    MIN_CHARS,
    MAX_CHARS,
    loading,
    searchPending,

    open,
    value,
    helpText,
    showMinCharsHint,
    showMaxCharsHint,
    selectedType,
    filtersOpen,
    setFiltersOpen,

    wrapperRef,
    panelRef,

    showResults,
    showResultsSkeleton,

    searchResults,
    visibleResults,
    hasMoreResults,
    loadingMoreResults,
    loadMoreError,

    filteredRecents,

    handleOpen,
    handleClose,
    handleChange,
    handleBlur,
    handlePressEnter,
    handleSearchClick,
    handleTypeSelect,
    handleViewMore,
    markSuppressOpenFromClear,

    addToRecentlyViewed,
    clearRecentlyViewed,

    accessScopeUsers,
    accessScopeFrameworks,
    accessScopeEntities,
    groupStatusMap,
    selectedFrameworkKeys,
    selectedResponsibleKeys,
    selectedEntityKeys,
    toggleFrameworkKey,
    toggleResponsibleKey,
    toggleEntityKey,
    selectAllFrameworkKeys,
    selectAllEntityKeys,
    selectAllResponsibleKeys,
    deselectFrameworkKeys,
    deselectEntityKeys,
    deselectResponsibleKeys,
    clearFrameworkKeys,
    clearEntityKeys,
    clearResponsibleKeys,
    showEndOfResultsHint,
  } = useGlobalSearchBar();
  const { t } = useTranslation("globalSearch");
  const [drawerRequest, setDrawerRequest] = useState(null);
  const [previewEvidence, setPreviewEvidence] = useState(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const leftScrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadingMoreResultsRef = useRef(loadingMoreResults);
  const loadMoreErrorRef = useRef(loadMoreError);
  loadingMoreResultsRef.current = loadingMoreResults;
  loadMoreErrorRef.current = loadMoreError;

  useEffect(() => {
    if (!open || !hasMoreResults || searchPending) return undefined;
    const root = leftScrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (loadingMoreResultsRef.current || loadMoreErrorRef.current) return;
        handleViewMore();
      },
      { root, rootMargin: "80px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    open,
    hasMoreResults,
    searchPending,
    visibleResults.length,
    loadMoreError,
    handleViewMore,
  ]);

  // Warm the FilePreviewer chunk while the user is in search so first preview is usually ready.
  useEffect(() => {
    if (!open) return;
    void import("./GlobalSearchEvidencePreviewContent");
  }, [open]);

  const openObligationDrawer = useCallback(
    (item) => {
      addToRecentlyViewed(item);
      setDrawerRequest({
        ...getObligationDrawerTarget(item),
        k: Date.now(),
      });
    },
    [addToRecentlyViewed]
  );

  const openEvidencePreview = useCallback(
    (item) => {
      addToRecentlyViewed(item);
      setPreviewEvidence(item);
    },
    [addToRecentlyViewed]
  );

  const searchEnabled = value.trim().length >= MIN_CHARS;
  const searchButtonActive = searchEnabled && !loading;
  const isActive = open;
  const showCharCounter = value.length >= MAX_CHARS - 20;

  const sectionTitleKey =
    RESULT_SECTION_TITLE_KEYS[selectedType] ||
    RESULT_SECTION_TITLE_KEYS[GLOBAL_SEARCH_TYPES.OBLIGATIONS];
  const supportedRecents = (filteredRecents || []).filter(
    (item) =>
      item?.type === GLOBAL_SEARCH_TYPES.OBLIGATIONS ||
      isFilesSearchType(item?.type)
  );
  const showingStatusText = useMemo(() => {
    if (!showResults || searchPending || searchResults.length === 0) return "";
    const count = searchResults.length;
    return hasMoreResults
      ? t("global_search.labels.showing_count_more", { count })
      : t("global_search.labels.showing_count", { count });
  }, [hasMoreResults, searchPending, searchResults.length, showResults, t]);

  const typeOptions = useMemo(
    () => [
      {
        label: t("global_search.labels.type_all"),
        value: GLOBAL_SEARCH_TYPES.ALL,
        Icon: AppstoreOutlined,
      },
      {
        label: t("global_search.labels.type_obligations"),
        value: GLOBAL_SEARCH_TYPES.OBLIGATIONS,
        Icon: ObligationsTypeIcon,
      },
      {
        label: t("global_search.labels.type_files"),
        value: GLOBAL_SEARCH_TYPES.FILES,
        Icon: FilesTypeIcon,
      },
    ],
    [t]
  );

  const typeMenuItems = useMemo(
    () =>
      typeOptions.map((opt) => {
        const Icon = opt.Icon;
        return {
          key: opt.value,
          label: (
            <span className="gsb2-type-menu-item">
              {Icon ? <Icon className="gsb2-type-menu-item-icon" /> : null}
              {opt.label}
            </span>
          ),
        };
      }),
    [typeOptions]
  );

  const filterSidebar = (
    <SearchFilterSidebar
      accessScopeFrameworks={accessScopeFrameworks}
      accessScopeEntities={accessScopeEntities}
      accessScopeUsers={accessScopeUsers}
      selectedFrameworkKeys={selectedFrameworkKeys}
      selectedEntityKeys={selectedEntityKeys}
      selectedResponsibleKeys={selectedResponsibleKeys}
      onToggleFramework={toggleFrameworkKey}
      onToggleEntity={toggleEntityKey}
      onToggleResponsible={toggleResponsibleKey}
      onSelectAllFrameworks={selectAllFrameworkKeys}
      onSelectAllEntities={selectAllEntityKeys}
      onSelectAllResponsibles={selectAllResponsibleKeys}
      onDeselectFrameworks={deselectFrameworkKeys}
      onDeselectEntities={deselectEntityKeys}
      onDeselectResponsibles={deselectResponsibleKeys}
      onClearFrameworks={clearFrameworkKeys}
      onClearEntities={clearEntityKeys}
      onClearResponsibles={clearResponsibleKeys}
      t={t}
    />
  );

  return (
    <div
      ref={wrapperRef}
      className={`gsb2-wrapper${open ? " gsb2-wrapper--active" : ""}`}
      onMouseDownCapture={(e) => {
        if (
          !open &&
          e.target.closest?.(
            ".ant-input-clear-icon, .gsb2-input-clearIcon"
          )
        ) {
          markSuppressOpenFromClear();
        }
      }}
    >
      <Input
        id="global-search-input"
        className="gsb2-input"
        placeholder={t("global_search.placeholders.search_input")}
        autoComplete="off"
        prefix={
          <SearchOutlined
            className="gsb2-input-prefixIcon"
            style={{ fontSize: 14 }}
          />
        }
        value={value}
        maxLength={MAX_CHARS}
        onChange={handleChange}
        onBlur={handleBlur}
        onPressEnter={handlePressEnter}
        onFocus={handleOpen}
        onClick={(e) => {
          if (
            e.target.closest?.(
              ".ant-input-clear-icon, .gsb2-input-clearIcon"
            )
          ) {
            return;
          }
          handleOpen();
        }}
        allowClear={{
          clearIcon: (
            <CloseOutlined
              className="gsb2-input-clearIcon"
              style={{ fontSize: 12 }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!open) markSuppressOpenFromClear();
              }}
            />
          ),
        }}
        suffix={
          <span className="gsb2-suffix" onMouseDown={(e) => e.preventDefault()}>
            {showCharCounter && (
              <span
                className={`gsb2-char-count${showMaxCharsHint ? " gsb2-char-count--max" : ""}`}
                aria-live="polite"
              >
                {value.length}/{MAX_CHARS}
              </span>
            )}
            {isActive ? (
              <button
                type="button"
                className={`gsb2-search-btn${searchButtonActive ? " gsb2-search-btn--active" : ""}`}
                onClick={handleSearchClick}
                disabled={!searchEnabled || loading}
                title={t("global_search.tooltips.search_button_min_chars", {
                  min: MIN_CHARS,
                })}
              >
                {loading
                  ? t("global_search.hints.searching")
                  : t("global_search.buttons.search")}
              </button>
            ) : (
              <>
                <span className="gsb2-input-divider" aria-hidden="true" />
                <Dropdown
                  trigger={["click"]}
                  open={typeMenuOpen}
                  onOpenChange={setTypeMenuOpen}
                  menu={{
                    selectable: true,
                    selectedKeys: [selectedType],
                    items: typeMenuItems,
                    onClick: ({ key }) => {
                      setTypeMenuOpen(false);
                      handleTypeSelect(key);
                      handleOpen();
                      setTimeout(() => {
                        document.getElementById("global-search-input")?.focus();
                      }, 0);
                    },
                  }}
                >
                  <button
                    type="button"
                    className="gsb2-type-menu-trigger"
                    aria-label={t("global_search.actions.open_search_type_menu")}
                    aria-haspopup="menu"
                    aria-expanded={typeMenuOpen}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuOutlined className="gsb2-type-menu-trigger-icon" />
                  </button>
                </Dropdown>
              </>
            )}
          </span>
        }
      />

      {drawerRequest?.id && (
        <LazyObligationDetails
          key={drawerRequest.k}
          obligationId={drawerRequest.id}
          recurrenceId={drawerRequest.recurrenceId}
          displayMode="drawer"
          drawerWidth={1800}
          open={true}
          onClose={() => setDrawerRequest(null)}
        />
      )}

      {previewEvidence && (
        <Drawer
          className="gsb2-preview-drawer"
          open
          title={
            previewEvidence.file_name ||
            previewEvidence.title ||
            t("global_search.labels.unknown_evidence")
          }
          placement="right"
          destroyOnClose
          keyboard
          width="min(1100px, 96vw)"
          styles={{ body: PREVIEW_DRAWER_BODY_STYLES }}
          onClose={() => setPreviewEvidence(null)}
        >
          <Suspense
            fallback={
              <PreviewDrawerSkeleton
                label={t("global_search.hints.loading_preview")}
              />
            }
          >
            <GlobalSearchEvidencePreviewContent evidence={previewEvidence} />
          </Suspense>
        </Drawer>
      )}

      {open && (
        <div
          id={SEARCH_PANEL_ID}
          ref={panelRef}
          className="gsb2-panel"
          role="dialog"
          aria-label={t("global_search.titles.panel")}
        >
          <div className="gsb2-panel-header">
            <span className="gsb2-panel-title">
              {t("global_search.titles.panel")}
            </span>

            <div className="gsb2-typeRow">
              {typeOptions.map((opt) => {
                const active = opt.value === selectedType;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`gsb2-pill${active ? " gsb2-pill--active" : ""}`}
                    onClick={() => handleTypeSelect(opt.value)}
                    aria-pressed={active}
                  >
                    {Icon ? (
                      <Icon className="gsb2-pill-icon" aria-hidden="true" />
                    ) : null}
                    {opt.label}
                  </button>
                );
              })}
              <button
                type="button"
                className={`gsb2-filter-toggle${filtersOpen ? " gsb2-filter-toggle--active" : ""}`}
                onClick={() => setFiltersOpen((prev) => !prev)}
                aria-expanded={filtersOpen}
              >
                <FilterOutlined />
                {t("global_search.buttons.filters")}
              </button>
            </div>

            <button
              type="button"
              className="gsb2-close-btn"
              onClick={handleClose}
              aria-label={t("global_search.actions.close")}
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="gsb2-layout">
            <div className="gsb2-main">
              <div className="gsb2-results">
                <div className="gsb2-leftTop">
                  <div className="gsb2-leftTop-row">
                    <div className="gsb2-sectionTitle-group">
                      <span className="gsb2-sectionTitle">
                        {showResults
                          ? t(sectionTitleKey)
                          : t("global_search.labels.section_recently_viewed")}
                      </span>
                      {showingStatusText && (
                        <span className="gsb2-showing-status">
                          {showingStatusText}
                        </span>
                      )}
                    </div>
                    {!showResults && supportedRecents.length > 0 && (
                      <button
                        type="button"
                        className="gsb2-clear-recents"
                        onClick={clearRecentlyViewed}
                      >
                        {t("global_search.buttons.clear")}
                      </button>
                    )}
                  </div>
                  {(helpText || showMinCharsHint || showMaxCharsHint) && (
                    <div
                      className={`gsb2-help${showMinCharsHint || showMaxCharsHint ? " gsb2-help--warning" : ""}`}
                      role={helpText ? "alert" : "status"}
                    >
                      {(helpText ? t(helpText) : "") ||
                        (showMinCharsHint
                          ? t("global_search.hints.min_chars", { min: MIN_CHARS })
                          : t("global_search.hints.max_chars", { max: MAX_CHARS }))}
                    </div>
                  )}
                </div>

                <div
                  ref={leftScrollRef}
                  className="gsb2-leftScroll gsb2-scroll"
                >
                  {showResults || showResultsSkeleton ? (
                    <div
                      className={`gsb2-list${searchPending && visibleResults.length > 0 && !showResultsSkeleton ? " gsb2-list--pending" : ""}`}
                      role={showResultsSkeleton ? "status" : undefined}
                      aria-busy={showResultsSkeleton || undefined}
                    >
                      {showResultsSkeleton ? (
                        <ResultsSkeleton count={6} />
                      ) : (
                        <>
                          {visibleResults.map((item) => (
                            <SearchResultRow
                              key={item.hit_id || `${item.type}-${item.id}`}
                              item={item}
                              groupStatus={
                                groupStatusMap?.[item.group_id] ??
                                groupStatusMap?.[item.id]
                              }
                              onOpen={openObligationDrawer}
                              onPreview={openEvidencePreview}
                              t={t}
                            />
                          ))}

                          {hasMoreResults && !searchPending && (
                            <div className="gsb2-viewmore-wrap">
                              <button
                                type="button"
                                className="gsb2-viewmore-btn"
                                onClick={handleViewMore}
                                disabled={loadingMoreResults}
                                aria-busy={loadingMoreResults}
                              >
                                {loadingMoreResults ? (
                                  <>
                                    <Spin size="small" />
                                    {t("global_search.buttons.loading_more")}
                                  </>
                                ) : (
                                  t("global_search.buttons.load_more")
                                )}
                              </button>
                              {loadMoreError && (
                                <div className="gsb2-viewmore-error" role="alert">
                                  {t("global_search.messages.load_more_failed")}
                                </div>
                              )}
                              <div
                                ref={loadMoreSentinelRef}
                                className="gsb2-loadmore-sentinel"
                                aria-hidden="true"
                              />
                            </div>
                          )}

                          {showEndOfResultsHint && (
                            <div className="gsb2-end-hint" role="status">
                              {t("global_search.messages.end_of_results")}
                            </div>
                          )}
                        </>
                      )}

                      {!showResultsSkeleton &&
                        !searchPending &&
                        searchResults.length === 0 && (
                        <div className="gsb2-empty">
                          {t(
                            selectedType === GLOBAL_SEARCH_TYPES.FILES
                              ? "global_search.messages.no_file_results"
                              : "global_search.messages.no_results"
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="gsb2-list">
                      {supportedRecents.map((item) => (
                        <SearchResultRow
                          key={`${item.type}-${item.id}`}
                          item={item}
                          groupStatus={
                            groupStatusMap?.[item.group_id] ??
                            groupStatusMap?.[item.id]
                          }
                          onOpen={openObligationDrawer}
                          onPreview={openEvidencePreview}
                          t={t}
                        />
                      ))}

                      {supportedRecents.length === 0 && (
                        <div className="gsb2-empty">
                          {t("global_search.messages.no_recently_viewed")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="gsb2-sidebar gsb2-sidebar--desktop">
              {filterSidebar}
            </div>
          </div>

          {filtersOpen && (
            <div
              className="gsb2-filters-drawer-backdrop"
              role="presentation"
              onClick={() => setFiltersOpen(false)}
            >
              <div
                className="gsb2-sidebar gsb2-sidebar--mobile open"
                role="dialog"
                aria-label={t("global_search.buttons.filters")}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="gsb2-filters-drawer-header">
                  <span className="gsb2-sectionTitle">
                    {t("global_search.buttons.filters")}
                  </span>
                  <button
                    type="button"
                    className="gsb2-close-btn"
                    onClick={() => setFiltersOpen(false)}
                    aria-label={t("global_search.actions.close")}
                  >
                    <CloseOutlined />
                  </button>
                </div>
                {filterSidebar}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
